import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  listenToMessages,
  sendMessage,
  markMessagesAsRead,
} from "../firebase/firebase";
import styles from "./ChatRoom.module.css";

interface Message {
  id: string;
  senderId: string;
  text: string;
  timestamp: string;
  read?: boolean;
}

// 날짜 포맷팅 함수
const formatDateHeader = (timestamp: string) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  // 년, 월, 일 비교
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isToday) {
    return "오늘";
  } else if (isYesterday) {
    return "어제";
  } else {
    // 2023년 4월 29일 (화) 형식으로 반환
    const weekDays = ["일", "월", "화", "수", "목", "금", "토"];
    const weekDay = weekDays[date.getDay()];
    return `${date.getFullYear()}년 ${
      date.getMonth() + 1
    }월 ${date.getDate()}일 (${weekDay})`;
  }
};

// 시간이 1분 이내인지 확인하는 함수
const isSameTimeFrame = (timestamp1: string, timestamp2: string) => {
  const time1 = new Date(timestamp1).getTime();
  const time2 = new Date(timestamp2).getTime();
  const ONE_MINUTE = 60 * 1000; // 1분(밀리초)
  return Math.abs(time1 - time2) < ONE_MINUTE;
};

// 메시지를 날짜별로 그룹화하는 함수
const groupMessagesByDate = (messages: Message[]) => {
  const groups: { date: string; messages: Message[] }[] = [];

  messages.forEach((message) => {
    const messageDate = new Date(message.timestamp);
    const dateString = `${messageDate.getFullYear()}-${
      messageDate.getMonth() + 1
    }-${messageDate.getDate()}`;

    const existingGroup = groups.find((group) => group.date === dateString);
    if (existingGroup) {
      existingGroup.messages.push(message);
    } else {
      groups.push({
        date: dateString,
        messages: [message],
      });
    }
  });

  return groups;
};

// 시간 포맷팅 함수 (오전/오후 시:분 형식)
const formatTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // 오전/오후 표시
  const ampm = hours < 12 ? "오전" : "오후";

  // 12시간제로 변환
  const hour12 = hours % 12 || 12;

  // 분이 한 자리 숫자인 경우 앞에 0 추가
  const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;

  return `${ampm} ${hour12}:${minutesFormatted}`;
};

// 아바타 컴포넌트
const UserAvatar: React.FC<{
  userName: string;
  size?: number;
  className?: string;
}> = ({ userName, size = 40, className }) => {
  // 이름의 첫 글자 또는 기본값 가져오기
  const initial =
    userName && userName.length > 0 ? userName.charAt(0).toUpperCase() : "?";

  return (
    <div
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: "#ff866c",
        color: "white",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: `${size / 2}px`,
      }}
    >
      {initial}
    </div>
  );
};

const ChatRoom: React.FC = () => {
  const { chatRoomId } = useParams<{ chatRoomId: string }>();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  // 메시지 자동 스크롤
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // 메시지 렌더링 후 스크롤을 맨 아래로 내리기 위한 훅
  useEffect(() => {
    if (messages.length > 0 && !loading) {
      // 메시지가 로드되면 스크롤을 아래로 내리고,
      // 컴포넌트가 완전히 렌더링된 후 한 번 더 스크롤을 내림
      setTimeout(scrollToBottom, 100);

      // DOM이 완전히 업데이트 된 후 한 번 더 스크롤을 시도
      const timer = setTimeout(scrollToBottom, 500);
      return () => clearTimeout(timer);
    }
  }, [messages.length, loading, scrollToBottom]);

  // 상대방 ID 찾기
  const getOtherUserId = useMemo(() => {
    if (!currentUser || !chatRoomId) return "";

    // 채팅방 ID를 분석하여 상대방 ID 추출
    const participants = chatRoomId.split("_");

    // 채팅방 ID가 user1_user2 형식인 경우
    if (participants.length === 2) {
      return participants[0] === currentUser.uid
        ? participants[1]
        : participants[0];
    }

    // 메시지에서 상대방 찾기 (이전 방식 유지)
    if (messages.length > 0) {
      const otherMessage = messages.find(
        (msg) => msg.senderId !== currentUser.uid
      );
      return otherMessage ? otherMessage.senderId : "";
    }

    return "";
  }, [messages, currentUser, chatRoomId]);

  // 상대방의 읽지 않은 메시지 여부
  const hasUnreadMessages = useMemo(() => {
    if (!currentUser || messages.length === 0) return false;

    // 상대방이 보낸 메시지 중 가장 최근 메시지가 읽지 않은 상태인지 확인
    const otherMessages = messages.filter(
      (msg) => msg.senderId !== currentUser.uid
    );
    if (otherMessages.length === 0) return false;

    const latestOtherMessage = otherMessages[otherMessages.length - 1];
    return latestOtherMessage.read === false;
  }, [messages, currentUser]);

  // 페이지 가시성 변경 감지 (다른 탭으로 이동 등)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!currentUser || !chatRoomId) return;

    // 메시지 리스너 설정
    const unsubscribe = listenToMessages(chatRoomId, (messagesList) => {
      setMessages(messagesList);
      setLoading(false);
    });

    return () => {
      unsubscribe(); // 컴포넌트 언마운트 시 리스너 해제
    };
  }, [currentUser, chatRoomId]);

  // 채팅방이 활성화되어 있을 때 메시지 읽음 처리
  useEffect(() => {
    if (!currentUser || !chatRoomId || !isVisible || messages.length === 0)
      return;

    // 상대방이 보낸 메시지 중 읽지 않은 메시지 ID 수집
    const unreadMessageIds = messages
      .filter((msg) => msg.senderId !== currentUser.uid && msg.read === false)
      .map((msg) => msg.id);

    // 읽지 않은 메시지가 있으면 읽음 처리
    if (unreadMessageIds.length > 0) {
      markMessagesAsRead(chatRoomId, unreadMessageIds);
    }
  }, [messages, currentUser, chatRoomId, isVisible]);

  // 새 메시지 보내기
  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!newMessage.trim() || !currentUser || !chatRoomId || isSending)
        return;

      try {
        setIsSending(true);
        await sendMessage(chatRoomId, currentUser.uid, newMessage.trim());
        setNewMessage("");

        // 메시지 전송 후 입력 필드에 포커스 설정
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      } catch (error) {
        console.error("메시지 전송 오류:", error);
        alert("메시지를 보내는 중 오류가 발생했습니다.");
      } finally {
        setIsSending(false);
      }
    },
    [newMessage, currentUser, chatRoomId, isSending]
  );

  // 뒤로 가기
  const handleBack = useCallback(() => {
    navigate("/chat");
  }, [navigate]);

  // 메시지들을 날짜별로 그룹화 (useMemo로 최적화)
  const dateGroups = useMemo(() => {
    return groupMessagesByDate(messages);
  }, [messages]);

  // 마지막 메시지 정보 가져오기
  const lastMessage = useMemo(() => {
    if (messages.length === 0) return null;
    return messages[messages.length - 1];
  }, [messages]);

  // 메시지 말풍선 스타일 결정 함수
  const getBubbleStyleClass = useCallback(
    (
      isMyMessage: boolean,
      isFirstInSequence: boolean,
      isLastInSequence: boolean
    ) => {
      // 위아래로 단일 메시지인 경우
      const isSingleMessage = isFirstInSequence && isLastInSequence;

      if (isMyMessage) {
        if (isSingleMessage) {
          return styles.mySingleBubble;
        } else {
          return isLastInSequence
            ? styles.myBubbleLast
            : isFirstInSequence
            ? styles.myBubbleFirst
            : styles.myBubbleMiddle;
        }
      } else {
        if (isSingleMessage) {
          return styles.theirSingleBubble;
        } else {
          return isLastInSequence
            ? styles.theirBubbleLast
            : isFirstInSequence
            ? styles.theirBubbleFirst
            : styles.theirBubbleMiddle;
        }
      }
    },
    []
  );

  // 상대방 프로필 이미지 표시 여부 결정 함수
  const shouldShowSenderInfo = useCallback(
    (
      isMyMessage: boolean,
      messageIndex: number,
      dateGroup: { messages: Message[] }
    ) => {
      if (isMyMessage) return false;

      // 1. 상대방의 마지막 채팅인 경우
      const isLastMessageFromSender =
        messageIndex === dateGroup.messages.length - 1 ||
        dateGroup.messages[messageIndex + 1].senderId !==
          dateGroup.messages[messageIndex].senderId;

      // 2. 1분 이상 시간 간격이 있는 경우
      const hasTimeGap =
        messageIndex < dateGroup.messages.length - 1 &&
        !isSameTimeFrame(
          dateGroup.messages[messageIndex].timestamp,
          dateGroup.messages[messageIndex + 1].timestamp
        );

      // 3. 상대방의 첫 메시지인 경우
      const isFirstMessageFromSender =
        messageIndex === 0 ||
        dateGroup.messages[messageIndex - 1].senderId !==
          dateGroup.messages[messageIndex].senderId;

      return isFirstMessageFromSender || isLastMessageFromSender || hasTimeGap;
    },
    []
  );

  if (loading) {
    return (
      <div className={styles.chatRoomLoading}>메시지를 불러오는 중...</div>
    );
  }

  return (
    <div className={styles.chatRoomContainer}>
      {/* 헤더 */}
      <div className={styles.chatRoomHeader}>
        <button className={styles.backButton} onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15.5 19l-7-7 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className={styles.chatRoomProfile}>
          <div className={styles.chatRoomAvatar}>
            <UserAvatar userName={getOtherUserId} size={40} />
            {hasUnreadMessages && (
              <div className={styles.notificationDot}></div>
            )}
          </div>
          <div className={styles.chatRoomInfo}>
            <h3 className={styles.chatRoomTitle}>
              {getOtherUserId
                ? getOtherUserId.length > 5
                  ? `${getOtherUserId.substring(0, 5)}...`
                  : getOtherUserId
                : "채팅"}
            </h3>
            <span className={styles.chatRoomSubtitle}>
              {lastMessage
                ? `마지막 대화: ${formatTime(lastMessage.timestamp)}`
                : "새로운 대화를 시작하세요"}
            </span>
          </div>
        </div>
        <div className={styles.chatRoomActions}>
          <button className={styles.menuButton} aria-label="메뉴">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 13a1 1 0 100-2 1 1 0 000 2z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M19 13a1 1 0 100-2 1 1 0 000 2z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 13a1 1 0 100-2 1 1 0 000 2z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* 메시지 목록 */}
      {messages.length === 0 ? (
        <div className={styles.emptyChat}>
          <div className={styles.emptyChatIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24">
              <path
                d="M20,2H4C2.9,2 2,2.9 2,4V22L6,18H20C21.1,18 22,17.1 22,16V4C22,2.9 21.1,2 20,2M20,16H6L4,18V4H20V16Z"
                fill="#cccccc"
              />
            </svg>
          </div>
          <p>앞으로 주고 받는 대화가 여기에 표시됩니다</p>
        </div>
      ) : (
        <div className={styles.chatMessages}>
          {dateGroups.map((dateGroup, groupIndex) => (
            <div key={`date-${groupIndex}`} className="date-group">
              <div className={styles.dateHeader}>
                <span>{formatDateHeader(dateGroup.messages[0].timestamp)}</span>
              </div>

              {dateGroup.messages.map((message, messageIndex) => {
                const isMyMessage = message.senderId === currentUser?.uid;

                // 연속 메시지 여부 확인
                const isFirstInSequence =
                  messageIndex === 0 ||
                  dateGroup.messages[messageIndex - 1].senderId !==
                    message.senderId;

                const isLastInSequence =
                  messageIndex === dateGroup.messages.length - 1 ||
                  dateGroup.messages[messageIndex + 1].senderId !==
                    message.senderId;

                // 프로필 표시 여부
                const showSenderInfo = shouldShowSenderInfo(
                  isMyMessage,
                  messageIndex,
                  dateGroup
                );

                // 시간 표시 여부 (마지막 메시지이거나 다음 메시지가 다른 사람이거나 1분 이상 차이)
                const showTime =
                  messageIndex === dateGroup.messages.length - 1 ||
                  dateGroup.messages[messageIndex + 1].senderId !==
                    message.senderId ||
                  !isSameTimeFrame(
                    message.timestamp,
                    dateGroup.messages[messageIndex + 1].timestamp
                  );

                const bubbleStyleClass = getBubbleStyleClass(
                  isMyMessage,
                  isFirstInSequence,
                  isLastInSequence
                );

                return (
                  <div
                    key={message.id}
                    className={`${styles.messageItem} ${
                      isMyMessage ? styles.myMessage : styles.theirMessage
                    }`}
                  >
                    {!isMyMessage && showSenderInfo && (
                      <div className={styles.messageSenderAvatar}>
                        <UserAvatar userName={message.senderId} size={30} />
                      </div>
                    )}
                    {!isMyMessage && !showSenderInfo && (
                      <div className={styles.messageAvatarPlaceholder}></div>
                    )}
                    <div className={styles.messageContent}>
                      {!isMyMessage && showSenderInfo && (
                        <div className={styles.senderName}>
                          {message.senderId.length > 8
                            ? message.senderId.substring(0, 8)
                            : message.senderId}
                        </div>
                      )}
                      <div
                        className={`${styles.messageBubble} ${bubbleStyleClass}`}
                      >
                        {message.text}
                      </div>
                      {showTime && (
                        <div className={styles.messageTime}>
                          {isMyMessage && message.read === false && (
                            <span className={styles.readStatus}>안읽음</span>
                          )}
                          <span>{formatTime(message.timestamp)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* 메시지 입력 폼 */}
      <form className={styles.chatInputForm} onSubmit={handleSendMessage}>
        <button
          type="button"
          className={styles.addButton}
          aria-label="추가"
          disabled={isSending}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <input
          type="text"
          className={styles.chatInput}
          placeholder="메시지 보내기"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          disabled={isSending}
          ref={inputRef}
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={!newMessage.trim() || isSending}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default ChatRoom;
